import { Container, Graphics, Sprite, Text } from 'pixi.js';
import { gsap } from 'gsap';
import { countryByCode, hexToNumber } from '@fbwl/shared';
import { flagTextures } from '../core/textures.js';
import { createGlow } from '../fx/filters.js';

export interface FlagCardOptions {
  /** Card width in design pixels. Height follows the 4:3 flag ratio. */
  readonly width?: number;
  readonly showCode?: boolean;
  readonly showName?: boolean;
  readonly glow?: boolean;
}

/**
 * A single nation on screen.
 *
 * One class covers every size the show needs - the 194-card qualification grid,
 * the draw ceremony tiles, the two duellists and the champion plate - because a
 * nation must be visually identical wherever it appears. Only the width changes;
 * borders, corner radius and type all scale from it.
 */
export class FlagCard extends Container {
  readonly code: string;
  readonly primary: number;
  readonly accent: number;

  private readonly frame: Graphics;
  private readonly flag: Sprite;
  private readonly shine: Graphics;
  private readonly codeLabel: Text | null;
  private readonly nameLabel: Text | null;
  private readonly cardWidth: number;
  private readonly cardHeight: number;

  private highlighted = false;

  constructor(code: string, options: FlagCardOptions = {}) {
    super();

    const { width = 120, showCode = true, showName = false, glow = false } = options;

    this.code = code;
    this.cardWidth = width;
    this.cardHeight = width * 0.75;

    const country = countryByCode(code);
    this.primary = hexToNumber(country?.primaryColor ?? '#38bdf8');
    this.accent = hexToNumber(country?.accentColor ?? '#ffc94d');

    const radius = Math.max(3, width * 0.07);
    const half = { w: this.cardWidth / 2, h: this.cardHeight / 2 };

    // Drop plate: a dark card behind the flag so a white flag still has edges.
    const plate = new Graphics()
      .roundRect(-half.w, -half.h, this.cardWidth, this.cardHeight, radius)
      .fill({ color: 0x05080f, alpha: 0.92 });

    this.flag = new Sprite(flagTextures.get(code));
    this.flag.anchor.set(0.5);
    this.flag.width = this.cardWidth;
    this.flag.height = this.cardHeight;

    const mask = new Graphics()
      .roundRect(-half.w, -half.h, this.cardWidth, this.cardHeight, radius)
      .fill(0xffffff);

    const flagHolder = new Container();
    flagHolder.addChild(this.flag);
    flagHolder.mask = mask;

    // Angled gloss, the "premium hardware" cue from the brief's Apple reference.
    this.shine = new Graphics()
      .poly([
        -half.w,
        -half.h,
        -half.w + this.cardWidth * 0.42,
        -half.h,
        -half.w + this.cardWidth * 0.12,
        half.h,
        -half.w,
        half.h,
      ])
      .fill({ color: 0xffffff, alpha: 0.09 });
    this.shine.mask = mask;

    this.frame = new Graphics()
      .roundRect(-half.w, -half.h, this.cardWidth, this.cardHeight, radius)
      .stroke({ width: Math.max(1.2, width * 0.018), color: this.accent, alpha: 0.85 });

    this.addChild(plate, flagHolder, this.shine, mask, this.frame);

    this.codeLabel = showCode ? this.createCodeLabel(width) : null;
    if (this.codeLabel) this.addChild(this.codeLabel);

    this.nameLabel = showName ? this.createNameLabel(width, country?.name ?? code) : null;
    if (this.nameLabel) this.addChild(this.nameLabel);

    if (glow) this.filters = [createGlow(this.accent, 1.6, Math.max(8, width * 0.18))];

    // Swap in the real flag as soon as it decodes; the generated card is only
    // ever a stand-in, never a permanent state.
    if (!flagTextures.has(code)) {
      void flagTextures.load(code).then((texture) => {
        if (this.destroyed) return;
        this.flag.texture = texture;
        this.flag.width = this.cardWidth;
        this.flag.height = this.cardHeight;
      });
    }

    this.eventMode = 'none';
  }

  /** Unscaled design width the card was built at. */
  get baseWidth(): number {
    return this.cardWidth;
  }

  get baseHeight(): number {
    return this.cardHeight;
  }

  /** Current on-screen size of the plate, ignoring labels and glow padding. */
  get plateWidth(): number {
    return this.cardWidth * this.scale.x;
  }

  get plateHeight(): number {
    return this.cardHeight * this.scale.y;
  }

  /** Ring the card in gold and lift it. Used for winners and spotlights. */
  setHighlight(active: boolean, color = this.accent): void {
    if (this.highlighted === active) return;
    this.highlighted = active;

    this.filters = active ? [createGlow(color, 3.4, Math.max(12, this.cardWidth * 0.26))] : [];
    gsap.to(this.scale, {
      x: active ? 1.08 : 1,
      y: active ? 1.08 : 1,
      duration: 0.45,
      ease: 'back.out(2)',
      overwrite: 'auto',
    });
  }

  /** Entry animation. `delay` staggers a grid without a timeline per card. */
  enter(delay = 0): gsap.core.Tween {
    this.alpha = 0;
    this.scale.set(0.4);
    this.rotation = (Math.random() - 0.5) * 0.6;

    gsap.to(this, { alpha: 1, rotation: 0, duration: 0.5, delay, ease: 'power2.out' });
    return gsap.to(this.scale, {
      x: 1,
      y: 1,
      duration: 0.62,
      delay,
      ease: 'back.out(1.7)',
    });
  }

  /** Knocked out: desaturate, drop, spin away. Returns when it is finished. */
  eliminate(delay = 0): gsap.core.Tween {
    gsap.to(this, {
      alpha: 0,
      rotation: (Math.random() - 0.5) * 2.4,
      duration: 0.7,
      delay,
      ease: 'power2.in',
    });
    gsap.to(this.scale, { x: 0.15, y: 0.15, duration: 0.7, delay, ease: 'power2.in' });
    return gsap.to(this, {
      y: this.y + 90,
      duration: 0.7,
      delay,
      ease: 'power2.in',
    });
  }

  /** Nudge on a scoring strike. */
  react(direction: 1 | -1): void {
    gsap.fromTo(
      this,
      { x: this.x + direction * 16 },
      { x: this.x, duration: 0.5, ease: 'elastic.out(1, 0.4)', overwrite: 'auto' },
    );
  }

  private createCodeLabel(width: number): Text {
    const label = new Text({
      text: this.code,
      style: {
        fontFamily: 'Bahnschrift, "Segoe UI", system-ui, sans-serif',
        fontSize: Math.max(11, width * 0.2),
        fontWeight: '700',
        fill: 0xffffff,
        letterSpacing: width * 0.02,
        dropShadow: {
          color: 0x000000,
          alpha: 0.85,
          blur: 4,
          distance: 1,
          angle: Math.PI / 2,
        },
      },
    });
    label.anchor.set(0.5);
    label.y = this.cardHeight / 2 + label.height * 0.62;
    return label;
  }

  private createNameLabel(width: number, name: string): Text {
    const label = new Text({
      text: name.toUpperCase(),
      style: {
        fontFamily: 'Bahnschrift, "Segoe UI", system-ui, sans-serif',
        fontSize: Math.max(9, width * 0.09),
        fill: 0x9bb0cc,
        letterSpacing: width * 0.012,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: width * 1.6,
      },
    });
    label.anchor.set(0.5, 0);
    label.y = this.cardHeight / 2 + width * 0.24;
    return label;
  }
}
